import { useEffect, useRef, useCallback, type RefObject } from 'react';
import { peerConnection } from '../../services/peerConnection';
import { useRdpStore } from '../../stores/rdpStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { logger } from '@/lib/logger';

const TAG = 'RdpInputOverlay';

// Throttle mouse moves to ~60Hz
const MOUSE_THROTTLE_MS = 16;

interface RdpInputOverlayProps {
  videoRef: RefObject<HTMLVideoElement | null>;
}

/**
 * Transparent overlay that captures mouse and keyboard events,
 * maps coordinates to remote screen space, and sends them
 * to the host via WebRTC data channel commands.
 */
export default function RdpInputOverlay({ videoRef }: RdpInputOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const lastMouseSendRef = useRef(0);
  const pendingMouseRef = useRef<{ x: number; y: number } | null>(null);
  const mouseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const remoteScreenWidth = useRdpStore((s) => s.remoteScreenWidth);
  const remoteScreenHeight = useRdpStore((s) => s.remoteScreenHeight);
  const remoteScaleFactor = useRdpStore((s) => s.remoteScaleFactor);
  const clipboardSync = useSettingsStore((s) => s.rdp.clipboardSync);

  /**
   * Map client viewport coordinates to host screen coordinates,
   * accounting for letterboxing/pillarboxing from object-fit: contain.
   */
  const mapCoordinates = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const video = videoRef.current;
      if (!video || remoteScreenWidth === 0 || remoteScreenHeight === 0) return null;

      const rect = video.getBoundingClientRect();

      // Calculate the actual rendered video dimensions (accounting for object-fit: contain)
      const videoAspect = remoteScreenWidth / remoteScreenHeight;
      const containerAspect = rect.width / rect.height;

      let renderWidth: number;
      let renderHeight: number;
      let offsetX: number;
      let offsetY: number;

      if (containerAspect > videoAspect) {
        // Pillarboxing (black bars on sides)
        renderHeight = rect.height;
        renderWidth = rect.height * videoAspect;
        offsetX = (rect.width - renderWidth) / 2;
        offsetY = 0;
      } else {
        // Letterboxing (black bars top/bottom)
        renderWidth = rect.width;
        renderHeight = rect.width / videoAspect;
        offsetX = 0;
        offsetY = (rect.height - renderHeight) / 2;
      }

      // Position relative to rendered video area
      const relX = clientX - rect.left - offsetX;
      const relY = clientY - rect.top - offsetY;

      // Check bounds
      if (relX < 0 || relX > renderWidth || relY < 0 || relY > renderHeight) return null;

      // Map to remote screen coordinates
      const x = Math.round((relX / renderWidth) * remoteScreenWidth * remoteScaleFactor);
      const y = Math.round((relY / renderHeight) * remoteScreenHeight * remoteScaleFactor);

      return { x, y };
    },
    [videoRef, remoteScreenWidth, remoteScreenHeight, remoteScaleFactor]
  );

  // Fire-and-forget input via dedicated rdp-input data channel
  const sendRdpInput = useCallback((type: string, params: Record<string, unknown>) => {
    peerConnection.sendRdpInput({ type, ...params });
  }, []);

  // Fallback: use request/response for commands that need a reply (clipboard)
  const sendRdpCommand = useCallback((command: string, params: Record<string, unknown>) => {
    peerConnection.sendCommand(command, params).catch((err) => {
      logger.warn(TAG, `RDP command ${command} failed:`, err);
    });
  }, []);

  // Mouse move (throttled)
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const coords = mapCoordinates(e.clientX, e.clientY);
      if (!coords) return;

      const now = performance.now();
      if (now - lastMouseSendRef.current >= MOUSE_THROTTLE_MS) {
        lastMouseSendRef.current = now;
        sendRdpInput('rdp_mouse_move', coords);
        pendingMouseRef.current = null;
      } else {
        // Queue the latest position
        pendingMouseRef.current = coords;
        if (!mouseTimerRef.current) {
          mouseTimerRef.current = setTimeout(() => {
            mouseTimerRef.current = null;
            if (pendingMouseRef.current) {
              lastMouseSendRef.current = performance.now();
              sendRdpInput('rdp_mouse_move', pendingMouseRef.current);
              pendingMouseRef.current = null;
            }
          }, MOUSE_THROTTLE_MS);
        }
      }
    },
    [mapCoordinates, sendRdpInput]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      const coords = mapCoordinates(e.clientX, e.clientY);
      if (!coords) return;
      const button = e.button === 0 ? 'left' : e.button === 1 ? 'middle' : 'right';
      sendRdpInput('rdp_mouse_button', { ...coords, button, action: 'down' });
    },
    [mapCoordinates, sendRdpInput]
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      const coords = mapCoordinates(e.clientX, e.clientY);
      if (!coords) return;
      const button = e.button === 0 ? 'left' : e.button === 1 ? 'middle' : 'right';
      sendRdpInput('rdp_mouse_button', { ...coords, button, action: 'up' });
    },
    [mapCoordinates, sendRdpInput]
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      sendRdpInput('rdp_mouse_scroll', {
        delta_x: Math.round(e.deltaX),
        delta_y: Math.round(e.deltaY),
      });
    },
    [sendRdpInput]
  );

  const handleContextMenu = useCallback((e: MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Escape hatch: Ctrl+Alt+Shift releases input capture
      if (e.ctrlKey && e.altKey && e.shiftKey) return;

      e.preventDefault();
      e.stopPropagation();
      sendRdpInput('rdp_key_event', {
        key: e.key,
        code: e.code,
        action: 'down',
        modifiers: {
          ctrl: e.ctrlKey,
          alt: e.altKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        },
      });
    },
    [sendRdpInput]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      sendRdpInput('rdp_key_event', {
        key: e.key,
        code: e.code,
        action: 'up',
        modifiers: {
          ctrl: e.ctrlKey,
          alt: e.altKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        },
      });
    },
    [sendRdpInput]
  );

  // Clipboard: intercept paste → send to host, intercept copy → get from host
  const handlePaste = useCallback(
    async (e: ClipboardEvent) => {
      if (!clipboardSync) return;
      e.preventDefault();
      const text = e.clipboardData?.getData('text/plain');
      if (text) {
        sendRdpCommand('rdp_clipboard_set', { content: text });
        // Trigger Ctrl+V on the remote so the paste executes in the focused app
        sendRdpInput('rdp_key_event', {
          key: 'v',
          code: 'KeyV',
          action: 'press',
          modifiers: { ctrl: true, alt: false, shift: false, meta: false },
        });
      }
    },
    [clipboardSync, sendRdpCommand]
  );

  const handleCopy = useCallback(
    async (e: ClipboardEvent) => {
      if (!clipboardSync) return;
      e.preventDefault();
      try {
        const remoteText = await peerConnection.sendCommand<string>('rdp_clipboard_get', {});
        if (remoteText && e.clipboardData) {
          e.clipboardData.setData('text/plain', remoteText);
        } else if (remoteText) {
          // Fallback: write to clipboard API
          await navigator.clipboard.writeText(remoteText);
        }
      } catch (err) {
        logger.warn(TAG, 'Failed to get remote clipboard:', err);
      }
    },
    [clipboardSync]
  );

  // Attach event listeners
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    overlay.addEventListener('mousemove', handleMouseMove);
    overlay.addEventListener('mousedown', handleMouseDown);
    overlay.addEventListener('mouseup', handleMouseUp);
    overlay.addEventListener('wheel', handleWheel, { passive: false });
    overlay.addEventListener('contextmenu', handleContextMenu);
    overlay.addEventListener('keydown', handleKeyDown);
    overlay.addEventListener('keyup', handleKeyUp);
    overlay.addEventListener('paste', handlePaste as unknown as EventListener);
    overlay.addEventListener('copy', handleCopy as unknown as EventListener);

    // Auto-focus for keyboard capture
    overlay.focus();

    return () => {
      overlay.removeEventListener('mousemove', handleMouseMove);
      overlay.removeEventListener('mousedown', handleMouseDown);
      overlay.removeEventListener('mouseup', handleMouseUp);
      overlay.removeEventListener('wheel', handleWheel);
      overlay.removeEventListener('contextmenu', handleContextMenu);
      overlay.removeEventListener('keydown', handleKeyDown);
      overlay.removeEventListener('keyup', handleKeyUp);
      overlay.removeEventListener('paste', handlePaste as unknown as EventListener);
      overlay.removeEventListener('copy', handleCopy as unknown as EventListener);
      if (mouseTimerRef.current) clearTimeout(mouseTimerRef.current);
    };
  }, [handleMouseMove, handleMouseDown, handleMouseUp, handleWheel, handleContextMenu, handleKeyDown, handleKeyUp, handlePaste, handleCopy]);

  return (
    <div
      ref={overlayRef}
      tabIndex={0}
      className="absolute inset-0 cursor-none outline-none"
      style={{ zIndex: 5 }}
    />
  );
}
